import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { isThemeMode, useTheme, type ThemeMode } from "@/lib/theme";

/**
 * Applies the signed-in user's saved theme and writes local changes back to the account.
 * localStorage remains the offline/logout cache so the preference survives sign-out.
 */
export function ThemeAccountSync() {
  const { user, patchUser } = useAuth();
  const { mode, setMode } = useTheme();
  const appliedUserId = useRef<string | null>(null);
  const skipPersist = useRef(false);

  useEffect(() => {
    if (!user) {
      appliedUserId.current = null;
      return;
    }
    if (appliedUserId.current === user.id) return;
    appliedUserId.current = user.id;

    const server: ThemeMode = isThemeMode(user.themePreference)
      ? user.themePreference
      : "system";

    if (server === mode) return;

    // Explicit account preference wins. If the account is still default "system"
    // and the browser cache has light/dark, keep local and let persist upload it.
    if (server !== "system" || mode === "system") {
      skipPersist.current = true;
      setMode(server);
    }
  }, [user, mode, setMode]);

  useEffect(() => {
    if (!user) return;
    if (skipPersist.current) {
      skipPersist.current = false;
      return;
    }
    if (user.themePreference === mode) return;
    let cancelled = false;
    void api<{ user: { themePreference?: ThemeMode } }>(
      "/api/auth/preferences",
      {
        method: "PUT",
        body: JSON.stringify({ themePreference: mode }),
      },
    )
      .then((res) => {
        if (cancelled) return;
        patchUser({ themePreference: res.user.themePreference ?? mode });
      })
      .catch(() => {
        /* keep local preference; retry on next change */
      });
    return () => {
      cancelled = true;
    };
  }, [mode, user, patchUser]);

  return null;
}
